import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, CheckCircle2, AlertTriangle, CreditCard, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Per-athlete subscription rollup for the parent dashboard.
 * Renders: "2 of 3 athletes subscribed" + a button to subscribe each
 * unsubscribed athlete. Surfaces legacy-block banner when applicable.
 */
export default function AthleteSubscriptionPanel() {
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(true);
    const [pickerOpenFor, setPickerOpenFor] = useState(null);  // athlete row
    const [requestOpenFor, setRequestOpenFor] = useState(null);  // {athlete, kind}

    const load = async () => {
        setBusy(true);
        try {
            const r = await axios.get(`${API}/me/athletes/subscriptions`, { withCredentials: true });
            setData(r.data);
        } catch (e) {
            // Silent — the panel is optional UX
            console.warn("subscription panel load failed", e);
        } finally {
            setBusy(false);
        }
    };
    useEffect(() => { load(); }, []);

    if (busy) {
        return (
            <div data-testid="athlete-subs-loading" className="border border-zinc-900 bg-zinc-950 p-5 mb-6 flex items-center gap-3 text-zinc-500 text-sm">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading subscriptions…
            </div>
        );
    }
    if (!data || data.athletes.length === 0) return null;

    const { athletes, summary, legacy_blocked } = data;

    return (
        <div data-testid="athlete-subs-panel" className="border border-zinc-900 bg-zinc-950 p-5 mb-8">
            <div className="flex items-start justify-between gap-3 mb-4">
                <div>
                    <div className="text-[10px] tracking-[0.25em] uppercase text-brand-red inline-flex items-center gap-2 mb-1">
                        <span className="inline-block w-8 h-px bg-brand-red" /> Membership
                    </div>
                    <p className="text-white text-lg font-semibold">
                        <span data-testid="subs-rollup">{summary.subscribed} of {summary.total}</span>{" "}
                        athlete{summary.total === 1 ? "" : "s"} subscribed
                    </p>
                </div>
                {summary.unsubscribed === 0 && summary.total > 0 && (
                    <CheckCircle2 className="w-6 h-6 text-emerald-400 flex-shrink-0" data-testid="subs-all-subscribed" />
                )}
            </div>

            {legacy_blocked && (
                <div data-testid="legacy-block-banner" className="bg-amber-950/40 border border-amber-800 rounded p-3 mb-4 flex gap-2">
                    <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-amber-200">
                        <p className="font-semibold text-amber-300 mb-0.5">Membership upgrade required</p>
                        <p>We've moved to per-athlete subscriptions. Your old membership doesn't grant access anymore — please subscribe each athlete below.</p>
                    </div>
                </div>
            )}

            <ul className="space-y-2">
                {athletes.map((a) => (
                    <li
                        key={a.athlete_id}
                        data-testid={`subs-row-${a.athlete_id}`}
                        className="flex items-center justify-between gap-3 bg-zinc-900/60 border border-zinc-800 rounded px-3 py-2"
                    >
                        <div className="min-w-0">
                            <p className="text-white text-sm font-medium truncate">
                                {a.first_name} {a.last_name || ""}
                            </p>
                            <p className="text-xs text-zinc-500">
                                {a.subscribed ? "Subscribed" : "Not subscribed"}
                                {a.grade ? ` · ${a.grade}` : ""}
                            </p>
                        </div>
                        {a.subscribed ? (
                            <div className="flex items-center gap-2">
                                <span className="text-xs px-2 py-0.5 rounded bg-emerald-900/40 text-emerald-300" data-testid={`subs-badge-active-${a.athlete_id}`}>
                                    Active
                                </span>
                                <button
                                    type="button"
                                    onClick={() => setRequestOpenFor({ athlete: a })}
                                    className="text-xs text-zinc-400 hover:text-white underline underline-offset-2"
                                    data-testid={`subs-request-${a.athlete_id}`}
                                >
                                    Request change
                                </button>
                            </div>
                        ) : (
                            <button
                                type="button"
                                onClick={() => setPickerOpenFor(a)}
                                className="text-xs px-3 py-1.5 rounded bg-brand-red hover:bg-red-600 text-white flex items-center gap-1"
                                data-testid={`subs-subscribe-${a.athlete_id}`}
                            >
                                <CreditCard className="w-3 h-3" /> Subscribe
                            </button>
                        )}
                    </li>
                ))}
            </ul>

            {pickerOpenFor && (
                <PlanPickerModal
                    athlete={pickerOpenFor}
                    onClose={() => setPickerOpenFor(null)}
                />
            )}
            {requestOpenFor && (
                <ChangeRequestModal
                    athlete={requestOpenFor.athlete}
                    onClose={() => setRequestOpenFor(null)}
                    onDone={() => { setRequestOpenFor(null); toast.success("Request sent — your coach will follow up."); }}
                />
            )}
        </div>
    );
}

function PlanPickerModal({ athlete, onClose }) {
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(null);

    useEffect(() => {
        axios.get(`${API}/pricing/matrix`, { withCredentials: true })
            .then((r) => {
                const subs = (r.data?.categories || [])
                    .find((c) => c.key === "member_subscription")?.skus || [];
                setPlans(subs.filter((s) => s.active && s.price_cents > 0));
            })
            .catch(() => toast.error("Couldn't load plans"))
            .finally(() => setLoading(false));
    }, []);

    const subscribe = async (sku_id) => {
        setSubmitting(sku_id);
        try {
            const r = await axios.post(
                `${API}/me/athletes/${athlete.athlete_id}/subscribe`,
                { sku_id, origin_url: window.location.origin },
                { withCredentials: true },
            );
            if (r.data?.url) {
                window.location.href = r.data.url;
            }
        } catch (e) {
            toast.error("Couldn't start checkout", { description: e?.response?.data?.detail });
            setSubmitting(null);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="plan-picker-modal">
            <div className="bg-zinc-950 border border-zinc-800 rounded-lg max-w-md w-full p-6">
                <div className="flex items-start justify-between mb-4">
                    <div>
                        <h3 className="text-lg font-semibold text-white">Pick a plan</h3>
                        <p className="text-xs text-zinc-500 mt-0.5">For <b>{athlete.first_name} {athlete.last_name || ""}</b></p>
                    </div>
                    <button type="button" onClick={onClose} className="text-zinc-500 hover:text-white" data-testid="plan-picker-close">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {loading ? (
                    <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-emerald-400" /></div>
                ) : plans.length === 0 ? (
                    <p className="text-sm text-zinc-400">No subscription plans are available right now. Please contact your coach.</p>
                ) : (
                    <div className="space-y-2">
                        {plans.map((p) => (
                            <button
                                key={p.sku_id}
                                type="button"
                                onClick={() => subscribe(p.sku_id)}
                                disabled={submitting !== null}
                                className="w-full text-left bg-zinc-900 border border-zinc-800 hover:border-brand-red rounded p-3 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-between"
                                data-testid={`plan-${p.sku_id}`}
                            >
                                <div>
                                    <p className="text-white font-medium">{p.display_name}</p>
                                    {p.description && <p className="text-xs text-zinc-500 mt-0.5">{p.description}</p>}
                                </div>
                                <div className="text-right">
                                    <p className="text-white font-bold">${(p.price_cents / 100).toFixed(2)}</p>
                                    {submitting === p.sku_id && <Loader2 className="w-4 h-4 animate-spin text-emerald-400 ml-auto mt-1" />}
                                </div>
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}


function ChangeRequestModal({ athlete, onClose, onDone }) {
    const [plans, setPlans] = useState([]);
    const [kind, setKind] = useState("switch");
    const [targetSku, setTargetSku] = useState("");
    const [note, setNote] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        axios.get(`${API}/pricing/matrix`, { withCredentials: true })
            .then((r) => {
                const subs = (r.data?.categories || [])
                    .find((c) => c.key === "member_subscription")?.skus || [];
                setPlans(subs.filter((s) => s.active && s.price_cents > 0));
            })
            .catch(() => {});
    }, []);

    const submit = async () => {
        if (kind === "switch" && !targetSku) {
            toast.error("Pick a plan to switch to");
            return;
        }
        setBusy(true);
        try {
            await axios.post(
                `${API}/me/athletes/${athlete.athlete_id}/subscription/change-request`,
                { kind, target_sku_id: kind === "switch" ? targetSku : null, note: note.trim() || null },
                { withCredentials: true },
            );
            onDone();
        } catch (e) {
            toast.error("Couldn't send request", { description: e?.response?.data?.detail });
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="change-request-modal">
            <div className="bg-zinc-950 border border-zinc-800 rounded-lg max-w-md w-full p-6">
                <div className="flex items-start justify-between mb-4">
                    <div>
                        <h3 className="text-lg font-semibold text-white">Request a change</h3>
                        <p className="text-xs text-zinc-500 mt-0.5">For <b>{athlete.first_name} {athlete.last_name || ""}</b>. Your coach will reply.</p>
                    </div>
                    <button type="button" onClick={onClose} className="text-zinc-500 hover:text-white"><X className="w-5 h-5" /></button>
                </div>

                <div className="space-y-3">
                    <div className="flex gap-2" data-testid="cr-kind-toggle">
                        <button type="button" onClick={() => setKind("switch")} className={`flex-1 px-3 py-2 text-sm rounded border ${kind === "switch" ? "border-brand-red text-white bg-zinc-900" : "border-zinc-800 text-zinc-400"}`} data-testid="cr-kind-switch">Switch plan</button>
                        <button type="button" onClick={() => setKind("cancel")} className={`flex-1 px-3 py-2 text-sm rounded border ${kind === "cancel" ? "border-brand-red text-white bg-zinc-900" : "border-zinc-800 text-zinc-400"}`} data-testid="cr-kind-cancel">Cancel</button>
                    </div>

                    {kind === "switch" && (
                        <div>
                            <label className="block text-xs text-zinc-400 mb-1">Switch to which plan?</label>
                            <div className="space-y-1.5">
                                {plans.map((p) => (
                                    <label key={p.sku_id} className="flex items-center gap-2 bg-zinc-900 border border-zinc-800 rounded px-3 py-2 cursor-pointer hover:border-zinc-600">
                                        <input type="radio" name="target-sku" value={p.sku_id} checked={targetSku === p.sku_id} onChange={() => setTargetSku(p.sku_id)} data-testid={`cr-target-${p.sku_id}`} />
                                        <span className="flex-1 text-white text-sm">{p.display_name}</span>
                                        <span className="text-zinc-400 text-sm">${(p.price_cents / 100).toFixed(2)}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    )}

                    <div>
                        <label className="block text-xs text-zinc-400 mb-1">Optional note for your coach</label>
                        <textarea data-testid="cr-note" rows="2" value={note} onChange={(e) => setNote(e.target.value)} placeholder={kind === "cancel" ? "Why are you cancelling? (optional)" : "Anything we should know? (optional)"} className="w-full bg-zinc-900 border border-zinc-700 rounded px-3 py-2 text-sm" />
                    </div>
                </div>

                <p className="text-xs text-zinc-500 mt-4">
                    {kind === "cancel"
                        ? "We'll cancel at the end of your current billing period — you keep what you've already paid for."
                        : "The switch will take effect at your next billing cycle. No charge today."}
                </p>

                <div className="flex justify-end gap-2 mt-5">
                    <button type="button" onClick={onClose} disabled={busy} className="px-3 py-2 text-sm text-zinc-400 hover:text-white">Cancel</button>
                    <button type="button" onClick={submit} disabled={busy} className="px-4 py-2 text-sm bg-brand-red hover:bg-red-600 disabled:opacity-50 rounded flex items-center gap-2" data-testid="cr-submit">
                        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                        Send request
                    </button>
                </div>
            </div>
        </div>
    );
}
